import { useTranslation } from 'react-i18next'
import { organizacionesDe, cargosDe } from './contactosLogica'
import ComercialChip from './ComercialChip'
import ComercialCopiable from './ComercialCopiable'

// The contacts as a table (a card per contact on a phone): one row per contact,
// however many organizations they work at. Nombre always shows; the rest are the
// user's choice (`columnas`, see contactosLogica.COLUMNAS_OPCIONALES). Their
// organizations and cargos go in the same order, separated by "·". A click on a
// row opens its card; on an organization, that organization's; on an email or a
// phone, copies it (ComercialCopiable).
//   filas: already filtered, sorted and cut; orden: { campo, dir }; actual: the id whose card is open
export default function ComercialContactosLista({ filas, columnas, orden, onOrden, actual, onAbrir }) {
  const { t } = useTranslation('comercial')
  const col = (campo, texto) => (
    <th key={campo} className="px-4 py-2.5 font-semibold text-gray-600 whitespace-nowrap"
      aria-sort={orden.campo === campo ? (orden.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" onClick={() => onOrden(campo)} className="inline-flex items-center gap-1 hover:text-gray-900">
        {texto}
        <span className={`text-[10px] ${orden.campo === campo ? 'text-gray-700' : 'text-gray-300'}`}>
          {orden.campo === campo && orden.dir === 'desc' ? '▼' : '▲'}
        </span>
      </button>
    </th>
  )
  const nada = <span className="text-gray-300">—</span>
  const parar = (e) => e.stopPropagation()
  const organizaciones = (c) => {
    const vs = c.vinculos_activos || []
    if (!vs.length) return nada
    return vs.map((v, i) => (
      <span key={v.id}>
        {i > 0 && <span className="text-gray-300"> · </span>}
        <button type="button" title={v.ruta} onClick={e => { parar(e); onAbrir('org', v.organizacion_id) }}
          className={`text-left hover:underline ${v.organizacion?.archivada ? 'text-gray-400' : 'text-gray-700 hover:text-gray-900'}`}>
          {v.organizacion?.nombre_comercial}
        </button>
      </span>
    ))
  }

  // Each optional column: its header, its cell, and its text on a phone's card.
  const COLUMNAS = {
    organizaciones: { titulo: t('listaContactos.organizaciones'), celda: organizaciones },
    cargo: { titulo: t('listaContactos.cargo'), celda: (c) => cargosDe(c).join(' · ') || nada, texto: (c) => cargosDe(c).join(' · ') },
    correo: {
      titulo: t('listaContactos.correo'),
      celda: (c) => (c.correo_principal ? <ComercialCopiable valor={c.correo_principal} /> : nada), texto: (c) => c.correo_principal,
    },
    telefono: {
      titulo: t('listaContactos.telefono'), clase: 'whitespace-nowrap',
      celda: (c) => (c.telefono_principal ? <ComercialCopiable valor={c.telefono_principal} tipo="telefono" /> : nada),
      texto: (c) => c.telefono_principal,
    },
    etiquetas: {
      titulo: t('listaContactos.etiquetas'),
      celda: (c) => (c.etiquetas.length
        ? <div className="flex flex-wrap gap-1">{c.etiquetas.map(x => <ComercialChip key={x.id} variante="etiqueta">{x.nombre}</ComercialChip>)}</div>
        : nada),
    },
  }
  const visibles = columnas.filter(c => COLUMNAS[c])

  return (
    <>
      <div className="hidden sm:block bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-anchos-propios>
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left">
              {col('nombre', t('listaContactos.nombre'))}
              {visibles.map(c => col(c, COLUMNAS[c].titulo))}
            </tr>
          </thead>
          <tbody>
            {filas.map(c => (
              <tr key={c.id} tabIndex={0} onClick={() => onAbrir('contacto', c.id)}
                onKeyDown={e => { if (e.key === 'Enter') onAbrir('contacto', c.id) }}
                className={`border-b border-gray-50 last:border-0 cursor-pointer align-top focus:outline-none focus-visible:bg-gray-50
                  ${c.id === actual ? 'bg-sky-50' : 'hover:bg-gray-50'} ${c.archivado ? 'text-gray-400' : 'text-gray-700'}`}>
                <td className="px-4 py-2.5 min-w-[12rem]"><Nombre c={c} /></td>
                {visibles.map(k => (
                  <td key={k} className={`px-4 py-2.5 ${COLUMNAS[k].clase || ''}`}>{COLUMNAS[k].celda(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="sm:hidden space-y-2">
        {filas.map(c => {
          const orgs = visibles.includes('organizaciones') ? organizacionesDe(c).filter(Boolean).join(' · ') : ''
          const datos = visibles.filter(k => COLUMNAS[k].texto).map(k => COLUMNAS[k].texto(c)).filter(Boolean)
          return (
            <li key={c.id}>
              <button type="button" onClick={() => onAbrir('contacto', c.id)}
                className={`w-full text-left bg-white rounded-xl border px-4 py-3 ${c.id === actual ? 'border-sky-300 bg-sky-50' : 'border-gray-200'}`}>
                <Nombre c={c} />
                {orgs && <p className="text-xs text-gray-600 mt-0.5 break-words">{orgs}</p>}
                {datos.length > 0 && <p className="text-xs text-gray-500 mt-0.5 break-words">{datos.join(' · ')}</p>}
                {visibles.includes('etiquetas') && c.etiquetas.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {c.etiquetas.map(x => <ComercialChip key={x.id} variante="etiqueta">{x.nombre}</ComercialChip>)}
                  </div>
                )}
              </button>
            </li>
          )
        })}
      </ul>
    </>
  )
}

function Nombre({ c }) {
  const { t } = useTranslation('comercial')
  return (
    <div className="flex items-start gap-2 min-w-0">
      <span className={`font-medium break-words ${c.archivado ? 'text-gray-500' : 'text-gray-900'}`}>{c.nombre_completo}</span>
      {c.archivado && <ComercialChip variante="archivado">{t('chips.archivado')}</ComercialChip>}
    </div>
  )
}
